// samplingEventForm/calibrationLogs/actions.js

import { v4 as uuidv4 } from "uuid";
import { WaterCalibrationLog } from "../../../../models/SamplingEvent/WaterCalibrationLog";

// api is an object provided by the parent factory with:
// - getCurrentEvent(): SamplingEvent | null
// - bump(patch?): void
// - setCalibrationLogUi(patch): void
export const makeCalibrationLogActions = (api) => ({
  /**
   * Opens the calibration log modal
   * @param {string} headerString - Modal header text
   * @param {boolean} isAddMode - True if adding new logs, false if editing existing
   */
  openModal: (headerString, isAddMode = false) => {
    api.setCalibrationLogUi({
      isModalOpen: true,
      modalHeaderString: headerString,
      isAddMode: isAddMode,
    });
    api.bump();
  },

  /**
   * Sets the current log ID being edited (used when editing existing log)
   */
  setCurrentLogId: (logId) => {
    api.setCalibrationLogUi({ currentLogId: logId });
    api.bump();
  },

  /**
   * Closes the modal and resets UI state
   */
  closeModal: () => {
    api.setCalibrationLogUi({
      isModalOpen: false,
      currentLogId: null,
      modalHeaderString: "",
      isAddMode: false,
    });
    api.bump();
  },

  /**
   * Opens delete confirmation modal for a specific log
   */
  openDeleteConfirm: (logId) => {
    api.setCalibrationLogUi({ isDeleteConfirmOpen: true, logToDelete: logId });
    api.bump();
  },

  /**
   * Closes delete confirmation modal
   */
  closeDeleteConfirm: () => {
    api.setCalibrationLogUi({ isDeleteConfirmOpen: false, logToDelete: null });
    api.bump();
  },

  // ============================================================================
  // TEMP STATE HELPER - Creates initial temp state for modal
  // ============================================================================

  /**
   * Creates the initial temporary state structure for the modal.
   * Called from CalibrationLogModal on mount.
   * @param {string|null} editLogId - ID of existing log to edit, or null if adding new
   * @param {object} user - Current user object with employee_id and name
   * @param {object} samplingEventData - The SamplingEvent instance
   * @returns {object} Initial tempState structure
   */
  createInitialTempState: (editLogId, user, samplingEventData) => {
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
    });
    const formattedTime = currentDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    // If editing an existing log, populate from it
    if (editLogId) {
      const existingLog = samplingEventData?.waterCalibrationLogs?.find(
        (log) => log.id === editLogId
      );

      if (existingLog) {
        const newTempId = uuidv4();
        return {
          sharedFields: {
            calibrationDate: existingLog.calibrationDate || formattedDate,
            calibrationTime: existingLog.calibrationTime || formattedTime,
            instrument: existingLog.instrument || "",
            employeeId: user?.employee_id || null,
            employeeName: user?.name || "",
          },
          logs: {
            [editLogId]: {
              fieldParameter: existingLog.fieldParameter || "",
              standardUsed: existingLog.standardUsed || "",
              verification: existingLog.verification || "",
              units: existingLog.units || "",
              passFail: existingLog.passFail || "",
              standardTop: null,
              standardBottom: null,
            },
          },
          existingLogIds: [editLogId],
        };
      }
    }

    // Adding new - create blank temp state with one new log entry
    const newTempId = uuidv4();
    return {
      sharedFields: {
        calibrationDate: formattedDate,
        calibrationTime: formattedTime,
        instrument: "",
        employeeId: user?.employee_id || null,
        employeeName: user?.name || "",
      },
      logs: {
        [newTempId]: {
          fieldParameter: "",
          standardUsed: "",
          verification: "",
          units: "",
          passFail: "",
          standardTop: null,
          standardBottom: null,
        },
      },
      existingLogIds: [], // No existing logs when adding new
    };
  },

  /**
   * Adds a new blank parameter row to tempState
   * @returns {string} The new temp log ID
   */
  createNewTempLogEntry: () => {
    const newTempId = uuidv4();
    return {
      id: newTempId,
      data: {
        fieldParameter: "",
        standardUsed: "",
        verification: "",
        units: "",
        passFail: "",
        standardTop: null,
        standardBottom: null,
      },
    };
  },

  // ============================================================================
  // COMMIT TEMP STATE TO ACTUAL DATA AND SERVER
  // ============================================================================

  /**
   * Commits all temporary state data to actual WaterCalibrationLog instances
   * and emits patches to the server.
   * @param {object} tempState - The temporary state from the modal
   * @param {object} samplingEventData - The SamplingEvent instance
   * @param {function} emitPatch - Function to emit patch to server
   * @returns {Promise<{ok: boolean, error?: string}>}
   */
  commitTempCalibrationLogs: async (
    tempState,
    samplingEventData,
    emitPatch
  ) => {
    const event = api.getCurrentEvent();
    if (!event) {
      return { ok: false, error: "No active sampling event" };
    }

    const { sharedFields, logs, existingLogIds } = tempState;
    const errors = [];

    // Process each log in tempState
    for (const [logId, logData] of Object.entries(logs)) {
      const isExisting = existingLogIds.includes(logId);
      let log;

      // Check if this log ID already exists in the event (handles double-save prevention)
      const existingLogInEvent = event.waterCalibrationLogs.find(
        (l) => l.id === logId
      );

      if (isExisting || existingLogInEvent) {
        // Update existing log
        log = existingLogInEvent;
        if (!log) {
          errors.push(`Log ${logId} not found`);
          continue;
        }
      } else {
        // Create new log in samplingEventData
        log = new WaterCalibrationLog({
          id: logId,
          sampling_event_id: event.id,
          employee_id: sharedFields.employeeId,
          employee_name: sharedFields.employeeName,
          created_at: new Date(),
          updated_at: new Date(),
        });
        event.waterCalibrationLogs.push(log);
      }

      // Apply shared fields to the log
      log.setCalibrationDate(sharedFields.calibrationDate);
      log.setCalibrationTime(sharedFields.calibrationTime);
      log.setInstrument(sharedFields.instrument);
      log.setEmployeeId(sharedFields.employeeId);
      log.setEmployeeName(sharedFields.employeeName);

      // Apply log-specific fields
      log.setFieldParameter(logData.fieldParameter);
      log.setStandardUsed(logData.standardUsed);
      log.setVerification(logData.verification);
      log.setUnits(logData.units);
      log.setPassFail(logData.passFail);

      // Emit patch to server (upsert handles both add and update)
      const action = isExisting ? "update" : "add";
      const patch = log.toPatch(action);

      if (typeof emitPatch === "function") {
        try {
          const result = await emitPatch(patch);
          // Handle both boolean returns (true/false) and object returns ({success: true/false})
          const isSuccess =
            result === true || result?.success === true || result?.ok === true;
          if (!isSuccess && result !== undefined) {
            // Only push error if we got an explicit failure, not if result is truthy
            const errorMsg =
              result?.error || result?.message || "Server returned an error";
            errors.push(`Failed to save log: ${errorMsg}`);
          }
        } catch (err) {
          errors.push(`Error saving log: ${err.message || err}`);
        }
      }
    }

    api.bumpRevision();

    // Even if there are server errors, data is saved locally
    // The broadcast will sync other users, and next refresh will reconcile
    if (errors.length > 0) {
      console.warn("Calibration log save warnings:", errors);
      // Return ok: true since local save succeeded, but include warnings
      return { ok: true, warnings: errors };
    }

    return { ok: true };
  },

  // ============================================================================
  // REMOVE LOG (from card list, not from within modal)
  // ============================================================================

  /**
   * Removes a calibration log from the data model and server.
   * Used when deleting from the log cards (not from within modal).
   */
  removeCalibrationLog: async (logId, emitPatch) => {
    const event = api.getCurrentEvent();
    const eventId = event?.id;
    if (!eventId) return { ok: false, error: "No active sampling event" };
    event.removeWaterCalibrationLog(logId);
    const patch = {
      patch_type: "water_calibration_log",
      action: "remove",
      id: logId,
    };
    if (typeof emitPatch === "function") {
      const result = await emitPatch(patch);
      api.bumpRevision();
      if (!result.success) {
        return { ok: false, error: result.error };
      } else {
        return { ok: true };
      }
    } else {
      api.bumpRevision();
      return { ok: false, error: "No emitPatch function provided" };
    }
  },

  // ============================================================================
  // WEBSOCKET BROADCAST HANDLER
  // ============================================================================

  /**
   * Handle incoming websocket broadcast for water_calibration_log patch_type.
   * Mutates the WaterCalibrationLog class instance directly using setter methods.
   * @param {object} patch - The patch data with snake_case keys
   * @param {function} applyPatchToObject - Helper to apply patch using setters
   */
  handleBroadcast: (patch, applyPatchToObject) => {
    const event = api.getCurrentEvent();
    if (!event) return;

    const { id, action } = patch;
    const logs = event.waterCalibrationLogs || [];

    // Handle add action - create new WaterCalibrationLog
    if (action === "add") {
      // Check if log already exists (in case we created it locally)
      const existingLog = logs.find((l) => l.id === id);
      if (existingLog) {
        // Update existing instead of duplicating
        applyPatchToObject(existingLog, patch, [
          "id",
          "action",
          "sampling_event_id",
        ]);
        api.bumpRevision();
        return;
      }
      const newLog = new WaterCalibrationLog(patch);
      logs.push(newLog);
      api.bumpRevision();
      return;
    }

    // Handle remove action - delete the WaterCalibrationLog
    if (action === "remove" || action === "delete") {
      const idx = logs.findIndex((l) => l.id === id);
      if (idx >= 0) {
        logs.splice(idx, 1);
        api.bumpRevision();
      }
      return;
    }

    // Handle update - find existing log and update via setters
    const log = logs.find((l) => l.id === id);
    if (!log) {
      // Log doesn't exist - create it as a fallback
      const newLog = new WaterCalibrationLog(patch);
      logs.push(newLog);
      api.bump();
      return;
    }
    // Apply patch fields using setter methods
    applyPatchToObject(log, patch, ["id", "action", "sampling_event_id"]);
    api.bump();
  },
});
